<template>
    <div class="contact-distributions-by-contact-property" :class="{ fullscreen: fullScreen }">
        <div v-for="property in data" :key="property.propertyId" class="property-chart">
            <div class="toolbar">
                <div class="left">
                    <h3>{{ property.propertyLabel }}</h3>
                    <div class="buttons">
                        <IconButton
                            :classNames="tableModes[property.propertyId] ? '' : 'selected'"
                            :onClick="() => setTableMode(property.propertyId, false)"
                            v-tippy="{ content: $t('report.chartView') }"
                        >
                            <md-icon>insights</md-icon>
                        </IconButton>
                        <IconButton
                            :classNames="tableModes[property.propertyId] ? 'selected' : ''"
                            :onClick="() => setTableMode(property.propertyId, true)"
                            v-tippy="{ content: $t('report.tableView') }"
                        >
                            <md-icon>table_view</md-icon>
                        </IconButton>
                        <IconButton :onClick="() => exportToExcel(property)" v-tippy="{ content: $t('report.download') }">
                            <md-icon>download</md-icon>
                        </IconButton>
                        <IconButton :onClick="switchToFullScreenMode" v-tippy="{ content: $t('report.fullScreen') }">
                            <md-icon v-if="!fullScreen">fullscreen</md-icon>
                            <md-icon v-else>fullscreen_exit</md-icon>
                        </IconButton>
                    </div>
                    <a-radio-group v-model="chartTypes[property.propertyId]" button-style="solid">
                        <a-radio-button value="bar">{{ $t('Sütun Grafik') }}</a-radio-button>
                        <a-radio-button value="pie">{{ $t('Pasta Grafik') }}</a-radio-button>
                    </a-radio-group>
                </div>
            </div>

            <div class="chart-wrapper">
                <DoubleBarChart
                    v-if="!tableModes[property.propertyId] && chartTypes[property.propertyId] === 'bar'"
                    :title="property.propertyLabel"
                    :xAxisData="getXAxisData(property)"
                    :yAxisData="getYAxisData(property)"
                    :series="getSeries(property)"
                />
                <div v-else-if="!tableModes[property.propertyId] && chartTypes[property.propertyId] === 'pie'" class="pie-chart-wrapper">
                    <v-chart :options="getPieChartOptions(property)" autoresize />
                </div>
                <TableView v-else :cols="getCols()" :rows="getRows(property)" />
            </div>

            <div class="stats">
                <StatCard :title="$t('En Çok Kullanılan')" :value="getStats(property).maxValue" />
                <StatCard :title="$t('En Az Kullanılan')" :value="getStats(property).minValue" />
                <StatCard
                    :title="$t('Toplam Kişi Sayısı')"
                    :value="formatStat(getStats(property).total).value"
                    unit="Kişi"
                />
            </div>
            <a-divider />
        </div>
        <a-empty v-if="!loading && (!data || data.length === 0)" />
    </div>
</template>

<script>
import moment from "moment";
import _ from "lodash";
import IconButton from "../../Pages/Settings/components/IconButton.vue";
import StatCard from "../../Pages/Chatbot/pages/reports/charts/StatCard.vue";
import TableView from "../../Pages/Chatbot/pages/reports/charts/TableView.vue";
import DoubleBarChart from "../../Pages/Chatbot/pages/reports/charts/DoubleBarChart.vue";
import { expertToExcel } from "../../../utils/utils";
import fscreen from "fscreen";

export default {
    name: "ContactDistributionsByContactProperty",
    components: {
        IconButton,
        StatCard,
        TableView,
        DoubleBarChart
    },
    props: {
        title: {
            type: String,
            required: true,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        data: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            chartTypes: {},
            tableModes: {},
            fullScreen: false,
            cancels: [],
            loadingInstance: null
        };
    },
    methods: {
        formatStat(value) {
            if (isNaN(value) || value === null || value === undefined) {
                return {
                    value: this.$t("report.noEnoughtData"),
                    unit: "",
                };
            }
            return {
                value: value,
                unit: this.$t("report.contact")
            };
        },
        getStats(property) {
            if (!property.distributions || property.distributions.length === 0) {
                return {
                    maxValue: this.$t("report.noEnoughtData"),
                    minValue: this.$t("report.noEnoughtData"),
                    total: 0
                };
            }

            const sortedData = _.orderBy(property.distributions, ["count"], ["desc"]);
            return {
                maxValue: sortedData[0].value || "N/A",
                minValue: sortedData[sortedData.length - 1].value || "N/A",
                total: _.sumBy(sortedData, 'count')
            };
        },
        getCols() {
            return [
                {
                    title: this.$t("report.value"),
                    dataIndex: "value",
                    key: "value",
                },
                {
                    title: this.$t("report.contactCount"),
                    dataIndex: "count",
                    key: "count",
                }
            ];
        },
        getRows(property) {
            return property.distributions.map(item => ({
                value: item.value || "N/A",
                count: item.count
            }));
        },
        getXAxisData(property) {
            const items = property.distributions || [];
            return {
                type: "category",
                data: items.map(item => item.value || "N/A"),
                axisLabel: {
                    interval: 0,
                    rotate: 30,
                },
                splitLine: {
                    show: false,
                }
            };
        },
        getYAxisData(property) {
            const items = property.distributions || [];
            const max = Math.max(...items.map(item => item.count));
            return {
                type: "value",
                max: max * 1.1,
                minInterval: 1,
                splitNumber: 10,
                splitLine: {
                    show: false,
                },
                axisLabel: {
                    rotate: -30,
                }
            };
        },
        getSeries(property) {
            const items = property.distributions || [];
            return [{
                colorBy: "data",
                showBackground: true,
                type: "bar",
                label: {
                    position: "right",
                    show: true,
                },
                data: items.map(item => item.count)
            }];
        },
        getPieChartOptions(property) {
            if (!property.distributions || property.distributions.length === 0) return {};

            const sortedData = _.orderBy(property.distributions, ["count"], ["desc"]);
            
            return {
                title: {
                    text: property.propertyLabel,
                    left: "center",
                },
                tooltip: {
                    trigger: "item",
                    formatter: "{b}: {c} ({d}%)",
                },
                series: [
                    {
                        type: "pie",
                        radius: "60%",
                        center: ["50%", "50%"],
                        data: sortedData.map(item => ({
                            name: item.value || "N/A",
                            value: item.count,
                        })),
                        emphasis: {
                            itemStyle: {
                                shadowBlur: 10,
                                shadowOffsetX: 0,
                                shadowColor: "rgba(0, 0, 0, 0.5)",
                            },
                        },
                    },
                ],
            };
        },
        switchToFullScreenMode() {
            if (this.fullScreen) {
                this.closeFullScreenMode();
            } else {
                const elem = document.querySelector(".contact-distributions-by-contact-property");
                fscreen.requestFullscreen(elem);
                this.fullScreen = true;
            }
        },
        closeFullScreenMode() {
            fscreen.exitFullscreen();
        },
        exportToExcel(property) {
            const excelData = this.getRows(property);
            const fileName = property.propertyLabel.toLowerCase().replace(/\s+/g, "-");
            expertToExcel(excelData, `property_distributions_${fileName}_${moment().format("YYYY-MM-DD")}.xlsx`, "Sheet 1");
        },
        setTableMode(propertyId, mode) {
            this.$set(this.tableModes, propertyId, mode);
        }
    },
    mounted() {
        this.loadingInstance = this.$veLoading({
            target: document.querySelector(".contact-distributions-by-contact-property"),
            name: "wave",
        });
        if (this.loading) {
            this.loadingInstance.show();
        }
    },
    created() {
        if (this.data) {
            this.data.forEach(property => {
                if (!this.chartTypes[property.propertyId]) {
                    this.$set(this.chartTypes, property.propertyId, 'bar');
                }
                if (this.tableModes[property.propertyId] === undefined) {
                    this.$set(this.tableModes, property.propertyId, false);
                }
            });
        }

        const fullscreenCallback = () => {
            this.fullScreen = fscreen.fullscreenElement !== null;
            this.$forceUpdate();
        };
        fscreen.addEventListener("fullscreenchange", fullscreenCallback);
        this.cancels.push(() => {
            fscreen.removeEventListener("fullscreenchange", fullscreenCallback);
        });
    },
    beforeDestroy() {
        this.cancels.forEach(cancel => cancel());
    },
    watch: {
        loading(val) {
            if (val) {
                this.loadingInstance.show();
            } else {
                this.loadingInstance.close();
            }
        },
        data: {
            immediate: true,
            handler(newData) {
                if (newData) {
                    newData.forEach(property => {
                        if (!this.chartTypes[property.propertyId]) {
                            this.$set(this.chartTypes, property.propertyId, 'bar');
                        }
                        if (this.tableModes[property.propertyId] === undefined) {
                            this.$set(this.tableModes, property.propertyId, false);
                        }
                    });
                }
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.contact-distributions-by-contact-property {
    display: flex;
    flex-direction: column;
    gap: 10px;

    &.fullscreen {
        background-color: #fff;
        padding: 20px;
        overflow-y: scroll;

        .chart-wrapper {
            height: calc(100vh - 200px);
        }
    }

    .property-chart {
        margin-bottom: 20px;

        .toolbar {
            display: flex;
            justify-content: space-between;
            margin-bottom: 16px;

            .left {
                display: flex;
                align-items: center;
                gap: 16px;

                h3 {
                    margin: 0;
                    font-size: 24px;
                }

                .buttons {
                    display: flex;
                    border: 1px solid var(--color-border-default);

                    .icon-button {
                        padding: 5px;
                        width: 40px;
                        height: 34px;
                        display: flex;
                        justify-content: center;
                        align-items: center;
                        border-right: 1px solid var(--color-border-default);
                        border-radius: unset !important;

                        &.selected {
                            background-color: var(--color-bg-info-low-hover);

                            i {
                                color: rgb(55, 162, 255);
                            }
                        }

                        &:hover {
                            background-color: var(--color-bg-neutral-pressed);
                        }
                    }

                    .icon-button:last-child {
                        border-right: none;
                    }
                }
            }
        }

        .chart-wrapper {
            min-height: 300px;
            height: auto;
        }

        .pie-chart-wrapper {
            width: 100%;
            height: 400px;
            ::v-deep .echarts {
                width: 100% !important;
            }
        }

        .stats {
            display: flex;
            flex-direction: row;
            gap: 10px;
            flex-wrap: wrap;
            margin-top: 16px;
        }
    }
}
</style> 